import { normalizeControlApiBase, readBootstrapConfig } from './bootstrapConfig';

const USER_API_BASE_ENV = import.meta.env.VITE_USER_API_BASE || '';
const PLATFORM_API_BASE_ENV =
  import.meta.env.VITE_PLATFORM_API_BASE || import.meta.env.VITE_API_BASE || '';
const ADMIN_TOKEN_ENV = import.meta.env.VITE_ADMIN_TOKEN || '';

function normalizeRequestPath(path) {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function buildApiUrl(base, path) {
  return `${base}${normalizeRequestPath(path)}`;
}

function resolveCloudflareToken(overrideToken = '') {
  const override = String(overrideToken || '').trim();
  if (override) return override;
  const config = readBootstrapConfig();
  return String(config?.cloudflareApiToken || '').trim();
}

function resolveAdminToken() {
  try {
    const localToken = globalThis.localStorage?.getItem('mirrorx_admin_token');
    if (localToken) return localToken.trim();
  } catch {
    // Ignore storage read failures.
  }
  return String(ADMIN_TOKEN_ENV || '').trim();
}

function dedupeBases(candidates = []) {
  const seen = new Set();
  const output = [];

  for (const candidate of candidates) {
    const normalized = normalizeControlApiBase(candidate);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    output.push(normalized);
  }

  return output;
}

function deriveWorkersProdBase(base) {
  const normalized = normalizeControlApiBase(base);
  if (!normalized) return '';

  try {
    const parsed = new URL(normalized);
    const labels = parsed.hostname.split('.');
    if (labels.length < 3) return '';

    const [scriptName, ...rest] = labels;
    const domainTail = rest.join('.');
    if (!domainTail.endsWith('workers.dev')) return '';
    if (!scriptName || scriptName.endsWith('-prod')) return '';

    return normalizeControlApiBase(`${parsed.protocol}//${scriptName}-prod.${domainTail}${parsed.pathname}`);
  } catch {
    return '';
  }
}

function expandDerivedBases(candidates = []) {
  const derived = [];
  for (const candidate of candidates) {
    const prodVariant = deriveWorkersProdBase(candidate);
    if (prodVariant) derived.push(prodVariant);
  }
  return dedupeBases([...candidates, ...derived]);
}

export function getUserControlApiCandidates({ preferredBase = '' } = {}) {
  const config = readBootstrapConfig();
  const candidates = expandDerivedBases(dedupeBases([
    preferredBase,
    config?.controlApiBase,
    USER_API_BASE_ENV,
  ]));
  const platformBase = normalizeControlApiBase(PLATFORM_API_BASE_ENV);
  if (!platformBase) return candidates;
  return candidates.filter((item) => item !== platformBase);
}

export function getUserControlApiBase() {
  return getUserControlApiCandidates()[0] || '';
}

export async function userCloudflareRequest(path, options = {}) {
  const {
    baseUrl,
    cloudflareToken,
    headers: inputHeaders,
    body,
    ...rest
  } = options;

  const resolvedBase = normalizeControlApiBase(baseUrl) || getUserControlApiBase();
  if (!resolvedBase) {
    throw new Error('未配置用户 Control API 地址');
  }

  const headers = new Headers(inputHeaders || {});
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const token = resolveCloudflareToken(cloudflareToken);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Keep browser requests CORS-compatible with legacy user workers.
  // The backend accepts Authorization bearer token already, so we do not
  // auto-add X-Cloudflare-Token here unless caller sets it explicitly.

  const adminToken = resolveAdminToken();
  if (adminToken && !headers.has('X-Admin-Token')) {
    headers.set('X-Admin-Token', adminToken);
  }

  const response = await fetch(buildApiUrl(resolvedBase, path), {
    ...rest,
    body,
    headers,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.error || payload?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return payload || {};
}

async function runPermissionChecks({
  controlApiBase,
  cloudflareApiToken,
}) {
  const checks = [];

  const checkItems = [
    {
      key: 'token_verify',
      label: 'Cloudflare token verify',
      path: '/bootstrap/cloudflare/verify',
    },
    {
      key: 'd1_read',
      label: 'D1 read routes count',
      path: '/routes/count',
    },
    {
      key: 'storage_status',
      label: 'KV/D1 storage status',
      path: '/storage/status',
    },
    {
      key: 'snapshot_read',
      label: 'Timetravel snapshot status',
      path: '/timetravel/snapshots/check-recent?minutes=5',
    },
  ];

  for (const item of checkItems) {
    try {
      const result = await userCloudflareRequest(item.path, {
        method: 'GET',
        baseUrl: controlApiBase,
        cloudflareToken: cloudflareApiToken,
      });

      checks.push({
        key: item.key,
        label: item.label,
        ok: true,
        message: 'ok',
        result,
      });
    } catch (error) {
      checks.push({
        key: item.key,
        label: item.label,
        ok: false,
        message: error?.message || 'failed',
      });
    }
  }

  const missing = checks.filter((item) => !item.ok).map((item) => item.key);
  const verifyResult = checks.find((item) => item.key === 'token_verify')?.result || {};

  return {
    ok: missing.length === 0,
    checks,
    missing,
    accountId: String(verifyResult?.accountId || ''),
    accountName: String(verifyResult?.accountName || ''),
    tokenStatus: String(verifyResult?.tokenStatus || ''),
    routeCount: Number(checks.find((item) => item.key === 'd1_read')?.result?.count || 0),
  };
}

function isNetworkFailureMessage(message = '') {
  const text = String(message || '').toLowerCase();
  return (
    text.includes('failed to fetch') ||
    text.includes('networkerror') ||
    text.includes('err_name_not_resolved') ||
    text.includes('timed out')
  );
}

function shouldPreferProbeFailure(current, candidate) {
  if (!current) return true;
  if (!candidate) return false;

  const currentNetwork = isNetworkFailureMessage(current?.error || '');
  const candidateNetwork = isNetworkFailureMessage(candidate?.error || '');

  if (currentNetwork && !candidateNetwork) return true;
  if (!currentNetwork && candidateNetwork) return false;

  const currentMissing = Array.isArray(current?.missing) ? current.missing.length : 999;
  const candidateMissing = Array.isArray(candidate?.missing) ? candidate.missing.length : 999;
  return candidateMissing < currentMissing;
}

export async function probeUserControlPermissions({
  controlApiBase,
  cloudflareApiToken,
} = {}) {
  const normalizedBase = normalizeControlApiBase(controlApiBase);
  if (!normalizedBase) {
    return {
      ok: false,
      checks: [],
      missing: ['control_api_base'],
      error: '请输入有效的 Control API 地址（示例：https://api.example.com/api）',
    };
  }

  try {
    const probe = await runPermissionChecks({
      controlApiBase: normalizedBase,
      cloudflareApiToken,
    });

    if (!probe.ok) {
      return {
        ...probe,
        controlApiBase: normalizedBase,
        error: `权限探测失败，缺少: ${probe.missing.join(', ')}`,
      };
    }

    return {
      ...probe,
      controlApiBase: normalizedBase,
    };
  } catch (error) {
    return {
      ok: false,
      controlApiBase: normalizedBase,
      checks: [],
      missing: ['unknown'],
      error: error?.message || '无法连接用户 Control API',
    };
  }
}

export async function autoProbeUserControlEndpoint({
  cloudflareApiToken,
  preferredBase,
} = {}) {
  const candidates = getUserControlApiCandidates({ preferredBase });
  if (!candidates.length) {
    return {
      ok: false,
      checks: [],
      missing: ['control_api_base'],
      error: '未检测到可用的 Control API 地址，请先部署用户侧 Control Worker',
      attempts: [],
    };
  }

  const attempts = [];
  let bestFailure = null;

  for (const base of candidates) {
    const probe = await probeUserControlPermissions({
      controlApiBase: base,
      cloudflareApiToken,
    });

    attempts.push({
      controlApiBase: base,
      ok: probe.ok,
      error: probe.error || '',
      missing: probe.missing || [],
    });

    if (probe.ok) {
      return {
        ...probe,
        attempts,
        autoDetected: true,
      };
    }

    if (shouldPreferProbeFailure(bestFailure, probe)) {
      bestFailure = probe;
    }
  }

  return {
    ...(bestFailure || {
      ok: false,
      checks: [],
      missing: ['unknown'],
      error: '无法连接用户 Control API',
    }),
    attempts,
    autoDetected: true,
  };
}

export async function verifyUserControlEndpoint({
  controlApiBase,
  cloudflareApiToken,
  autoDetect = true,
} = {}) {
  if (controlApiBase || !autoDetect) {
    return probeUserControlPermissions({
      controlApiBase,
      cloudflareApiToken,
    });
  }

  return autoProbeUserControlEndpoint({
    cloudflareApiToken,
    preferredBase: controlApiBase,
  });
}
